import { Link } from '@tanstack/react-router';
import { Compass } from 'lucide-react';
import { useState } from 'react';

import { AppShell } from '@/shared/layout/app-shell';
import { useStudio } from '@/shared/program/use-studio';
import { Alert } from '@/shared/ui/alert';
import { buttonVariants } from '@/shared/ui/button-variants';
import { PageSkeleton } from '@/shared/ui/skeleton';

import { useBillingPortal, useBillingStatus } from '../api/use-billing';
import { useProfile, useUpdateProfile } from '../api/use-profile';
import { PlanSummary } from '../components/plan-summary';
import { ProfileForm } from '../components/profile-form';

import '@/shared/ui/curriculum/curriculum.css';
import '../account.css';

export function AccountPage() {
  const profile = useProfile();
  const update = useUpdateProfile();
  const billing = useBillingStatus();
  const portal = useBillingPortal();
  const studio = useStudio();
  const [saved, setSaved] = useState(false);
  const path = studio.data?.path ?? null;
  const pathModules = path
    ? (studio.data?.modules.filter((module) => path.modules.includes(module.number)) ?? [])
    : [];
  return (
    <AppShell title="Account">
      <div className="studio-page account-page">
        <header className="studio-page-heading">
          <div>
            <p className="studio-eyebrow">Account</p>
            <h1>Your account</h1>
            <p className="studio-page-intro">Your details, your plan and your billing.</p>
          </div>
        </header>
        {profile.error ? (
          <Alert>We could not load your profile. {profile.error.message}</Alert>
        ) : null}
        <div className="account-grid">
          <section className="ui-card account-section" aria-labelledby="profile-title">
            <h2 id="profile-title">Profile</h2>
            {profile.data ? (
              <ProfileForm
                key={profile.data.displayName ?? ''}
                profile={profile.data}
                saving={update.isPending}
                error={update.error?.message ?? null}
                saved={saved && !update.isPending}
                onSave={(displayName) => {
                  setSaved(false);
                  update.mutate(displayName, { onSuccess: () => setSaved(true) });
                }}
              />
            ) : profile.isPending ? (
              <PageSkeleton label="Loading your profile…" />
            ) : null}
          </section>
          <section className="ui-card account-section" aria-labelledby="plan-title">
            <h2 id="plan-title">Plan</h2>
            {billing.data ? (
              billing.data.enabled ? (
                <PlanSummary
                  status={billing.data}
                  showPlansLink
                  portalBusy={portal.isPending}
                  onPortal={() =>
                    portal.mutate(undefined, {
                      onSuccess: (redirect) => window.location.assign(redirect.url),
                    })
                  }
                />
              ) : (
                <p className="text-fg-muted text-sm">Billing is not enabled in this environment.</p>
              )
            ) : billing.error ? (
              <Alert>We could not load your plan. {billing.error.message}</Alert>
            ) : (
              <PageSkeleton label="Loading your plan…" />
            )}
            {portal.error ? <Alert>{portal.error.message}</Alert> : null}
          </section>
          <section className="ui-card account-section" aria-labelledby="path-title">
            <h2 id="path-title">Your path</h2>
            {path ? (
              <p className="text-fg-muted text-sm leading-6">
                You start with Module {path.startModule}
                {pathModules.length > 0
                  ? ` and your path covers ${pathModules.map((module) => module.title).join(', ')}.`
                  : '.'}{' '}
                Every module stays open to you.
              </p>
            ) : (
              <p className="text-fg-muted text-sm leading-6">
                You have not chosen a path yet. A few questions point you to the module that matches
                what you can already do.
              </p>
            )}
            <Link
              to="/studio/path"
              className={buttonVariants({
                variant: 'secondary',
                size: 'sm',
                className: 'account-path-action',
              })}
            >
              <Compass size={13} strokeWidth={1.75} aria-hidden="true" />{' '}
              {path ? 'Change my path' : 'Choose my path'}
            </Link>
          </section>
        </div>
      </div>
    </AppShell>
  );
}
